import { useQuery } from '@tanstack/react-query'

import { conRespaldo } from '@/lib/campo/loUltimoQueSeSupo'
import { supabase } from '@/lib/supabase/cliente'
import type { OpcionSelector } from '@/shared/ui/Formulario/Selector'

/**
 * Listas de apoyo para los desplegables.
 *
 * Todo lo que se elige dentro de una orden —cliente, servicio, zona,
 * colaborador— sale de una tabla maestra. Nunca se escribe a mano: es lo que
 * permite que después se pueda agrupar por zona, medir por colaborador y
 * buscar por cliente sin depender de cómo lo escribió cada quien.
 *
 * Se cachean diez minutos porque los maestros casi no cambian durante una
 * jornada, y así abrir un formulario no dispara cuatro consultas.
 */

const CACHE_LARGO = 10 * 60 * 1000

type ResultadoOpciones = {
  opciones: OpcionSelector[]
  cargando: boolean
}

export function useOpcionesClientes(): ResultadoOpciones {
  const consulta = useQuery({
    queryKey: ['opciones', 'clientes'],
    staleTime: CACHE_LARGO,
    queryFn: async (): Promise<OpcionSelector[]> => {
      const { data, error } = await supabase
        .from('clientes')
        .select('id, nombre, documento')
        .eq('activo', true)
        .order('nombre')

      if (error) throw new Error(error.message)

      return (data ?? []).map((cliente) => ({
        valor: cliente.id,
        // El documento desambigua a dos clientes que se llaman parecido, cosa
        // que en una lista de cien nombres pasa más de lo que uno cree.
        etiqueta: `${cliente.nombre} — ${cliente.documento}`,
      }))
    },
  })

  return { opciones: consulta.data ?? [], cargando: consulta.isLoading }
}

export function useOpcionesServicios(): ResultadoOpciones {
  const consulta = useQuery({
    queryKey: ['opciones', 'servicios'],
    staleTime: CACHE_LARGO,
    // Con respaldo: el técnico levanta una obra en la calle y sin esta lista
    // no puede ni empezar —«faltan los servicios»—. Son once y no cambian.
    queryFn: () =>
      conRespaldo<OpcionSelector[]>('opciones-servicios', async () => {
      const { data, error } = await supabase
        .from('servicios')
        .select('id, nombre, codigo')
        .eq('activo', true)
        .order('nombre')

      if (error) throw new Error(error.message)

      return (data ?? []).map((servicio) => ({
        valor: servicio.id,
        // Con el código delante: es como se nombran entre ellos y como salen
        // en los informes. «Instalaciones internas» a secas obliga a traducir.
        etiqueta: servicio.codigo ? `${servicio.codigo} · ${servicio.nombre}` : servicio.nombre,
      }))
      }),
  })

  return { opciones: consulta.data ?? [], cargando: consulta.isLoading }
}

export function useOpcionesZonas(): ResultadoOpciones {
  const consulta = useQuery({
    queryKey: ['opciones', 'zonas'],
    staleTime: CACHE_LARGO,
    queryFn: async (): Promise<OpcionSelector[]> => {
      const { data, error } = await supabase
        .from('zonas')
        .select('id, nombre')
        .eq('activo', true)
        .order('nombre')

      if (error) throw new Error(error.message)

      return (data ?? []).map((zona) => ({ valor: zona.id, etiqueta: zona.nombre }))
    },
  })

  return { opciones: consulta.data ?? [], cargando: consulta.isLoading }
}

export function useOpcionesColaboradores(): ResultadoOpciones {
  const consulta = useQuery({
    queryKey: ['opciones', 'colaboradores'],
    staleTime: CACHE_LARGO,
    queryFn: async (): Promise<OpcionSelector[]> => {
      const { data, error } = await supabase
        .from('colaboradores')
        .select('id, nombre, cargo')
        .eq('activo', true)
        .order('nombre')

      if (error) throw new Error(error.message)

      return (data ?? []).map((colaborador) => ({
        valor: colaborador.id,
        etiqueta: colaborador.cargo
          ? `${colaborador.nombre} — ${colaborador.cargo}`
          : colaborador.nombre,
      }))
    },
  })

  return { opciones: consulta.data ?? [], cargando: consulta.isLoading }
}
